// features/chat-artifact-raw.js — ARTIFACT SESSION: RAW TAB
// ═══════════════════════════════════════════════════════════
// Conversation with artifact as context.
// Uses ChatSurface — only feature-specific logic lives here:
// artifact context in systemOverride, file upload, onResponseComplete.
// ═══════════════════════════════════════════════════════════
import { useRef } from 'react';
import { ChatSurface } from '../components/chat-surface';
import { useFileUpload } from '../hooks/use-file-upload';

export default function ChatArtifactRaw({
  userId, supabase, userRecord,
  artifact, sessionId,
  messages: externalMessages, setMessages: externalSetMessages,
  voiceListening, toggleVoice,
  onResponseComplete,
}) {
  const fileUpload = useFileUpload({ userId, supabase, sessionId });
  const sendTransitRef = useRef({ fileToSend: null });

  return (
    <ChatSurface
      userId={userId}
      supabase={supabase}
      sessionId={sessionId}
      userRecord={userRecord}
      messages={externalMessages}
      setMessages={externalSetMessages}
      typingAnimation

      beforeSend={async () => {
        const fileResult = await fileUpload.processForSend();
        const fileToSend = fileResult?.fileToSend || null;
        const fileData = fileResult?.fileData || null;
        sendTransitRef.current = { fileToSend };
        return { fileData };
      }}

      buildRequest={async ({ fileData, messagesSnapshot, signal }) => {
        const { data: { session: authSession } } = await supabase.auth.getSession();
        const cleanMessages = messagesSnapshot.map(m => ({
          role: m.role,
          content: typeof m.content === 'string' ? m.content : '',
        }));
        const artifactContext = artifact
          ? `[ARTIFACT CONTEXT]\nTitle: ${artifact.title}\nType: ${artifact.artifact_type || 'artifact'}\n${artifact.description ? `Description: ${artifact.description}\n` : ''}${artifact.content ? `Content:\n${artifact.content.substring(0, 5000)}` : ''}`
          : '';
        return {
          url: '/api/chat',
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authSession?.access_token || ''}` },
          body: {
            messages: cleanMessages,
            userId,
            sessionId,
            aiName: userRecord?.ai_name || 'Shift',
            userName: userRecord?.first_name || null,
            systemOverride: artifactContext || undefined,
            ...(fileData ? { fileData } : {}),
          },
          signal,
        };
      }}

      onResponseDone={(finalText) => {
        const { fileToSend } = sendTransitRef.current;
        if (fileToSend && finalText) fileUpload.updateDescription(finalText);
        sendTransitRef.current = { fileToSend: null };
        onResponseComplete?.();
      }}

      loadMessages={async () => {
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch(`/api/user/messages?sessionId=${encodeURIComponent(sessionId)}`, {
          headers: { 'Authorization': `Bearer ${session?.access_token || ''}` },
        });
        if (res.ok) {
          const { messages: data } = await res.json();
          return data || [];
        }
        return [];
      }}

      enableUpload
      enableDeepSearch
      enableDeepMode={false}
      enableVoice={!!toggleVoice}
      enableSlashCommands={false}
      placeholder="Explore this artifact with Shift..."
    />
  );
}
